import { LoadingOutlined, WechatOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Result, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const WeChatCallback = () => {
  const { wechatLogin } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // 获取URL参数
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          setStatus('error');
          setError(`微信授权失败: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setError('缺少授权码，请重新登录');
          return;
        }

        // 验证state参数
        const storedState = sessionStorage.getItem('wechat_login_state');
        if (state !== storedState) {
          setStatus('error');
          setError('状态参数不匹配，可能存在安全风险');
          return;
        }

        // 执行微信登录
        const result = await wechatLogin(code, state);

        if (result.success) {
          setStatus('success');
          setUserInfo(result.user);
          
          // 将回调数据存储到sessionStorage，供主窗口使用
          sessionStorage.setItem('wechat_callback_data', JSON.stringify({
            code,
            state,
            success: true,
            user: result.user,
            isNewUser: result.isNewUser
          }));
          
          // 通知父窗口（如果在弹窗中）
          if (window.opener) {
            window.opener.postMessage({
              type: 'WECHAT_LOGIN_SUCCESS',
              data: {
                user: result.user,
                isNewUser: result.isNewUser
              }
            }, '*');
          }
          
          // 延迟关闭窗口
          setTimeout(() => {
            if (window.opener) {
              window.close();
            } else {
              // 如果不是弹窗，重定向到主页
              window.location.href = '/';
            }
          }, 2000);
          
        } else {
          setStatus('error');
          setError(result.error);
        }

      } catch (error) {
        console.error('微信登录回调处理失败:', error);
        setStatus('error');
        setError('登录处理失败，请重试');
      } finally {
        // 清除状态
        sessionStorage.removeItem('wechat_login_state');
      }
    };

    handleCallback();
  }, [wechatLogin]);

  const handleRetry = () => {
    // 重新跳转到登录页面
    if (window.opener) {
      window.close();
    } else {
      window.location.href = '/login';
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Card style={{ textAlign: 'center', margin: '50px auto', maxWidth: 400 }}>
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              style={{ display: 'block', marginBottom: 16 }}
            />
            <h3>正在处理微信登录...</h3>
            <p style={{ color: '#666' }}>请稍候，正在验证您的登录信息</p>
          </Card>
        );

      case 'success':
        return (
          <Result
            status="success"
            title="微信登录成功！"
            subTitle={`欢迎 ${userInfo?.full_name || '微信用户'}！正在跳转...`}
            icon={<WechatOutlined style={{ color: '#07C160' }} />}
            extra={[
              <Button key="close" onClick={() => window.close()}>
                关闭窗口
              </Button>
            ]}
          />
        );

      case 'error':
        return (
          <Result
            status="error"
            title="微信登录失败"
            subTitle="登录过程中出现了问题"
            extra={[
              <Button type="primary" key="retry" onClick={handleRetry}>
                重新登录
              </Button>,
              <Button key="close" onClick={() => window.close()}>
                关闭窗口
              </Button>
            ]}
          >
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 16, textAlign: 'left' }}
              />
            )}
          </Result>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {renderContent()}
    </div>
  );
};

export default WeChatCallback;
